/**
 * The values a data-entry form sends (units slice 2b, docs/plans/2026-10-06_units-slice-2.md section 4). A reading names
 * no unit: its number is in the unit its field stores (the register's or the equipment's). The forms show that field in
 * the org's shown unit, so a number typed there is converted back to the stored unit before it is sent; text goes as
 * typed. Where the choice converts nothing (metric, a unit it shows as recorded, a field with no unit) the number is
 * exactly what was typed. Pure.
 */
import { shownFor, shownForFile, typedToStored } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";

/**
 * A numeric field publishes a numeric reading (value); everything else (string_label / categorical_enum / event_stream)
 * publishes a discrete reading (value_text). Mirrors the server's data_kind routing in registers.go /
 * readings_ingest.go.
 */
export function isNumericKind(dataKind: string): boolean {
  return (
    dataKind.includes("scalar") ||
    dataKind.includes("percentage") ||
    dataKind.includes("numeric") ||
    dataKind.includes("integer")
  );
}

/** What a form needs of a field. */
export interface EntryField {
  metric: string;
  label: string;
  unit?: string;
  data_kind: string;
}

/** The unit a field is read and typed in, shown beside it: the org's shown unit; the stored unit as written where nothing converts. */
export function entryUnit(f: Pick<EntryField, "unit">, prefs: DisplayPrefs | undefined): string {
  return shownFor(f.unit ?? "", prefs).unit;
}

/**
 * The readings a form sends: each filled field, a number converted from the shown unit to the field's stored unit, text
 * as typed; a blank field, and a number that is not one, left out.
 */
export function entryValues<F extends EntryField>(
  fields: readonly F[],
  rawOf: (f: F) => string,
  prefs: DisplayPrefs | undefined,
): { metric: string; value?: number; value_text?: string }[] {
  const out: { metric: string; value?: number; value_text?: string }[] = [];
  for (const f of fields) {
    const raw = rawOf(f).trim();
    if (raw === "") continue;
    if (isNumericKind(f.data_kind)) {
      const n = Number(raw);
      if (Number.isNaN(n)) continue;
      // A number the person typed in the shown unit, in the field's own.
      const stored = prefs ? typedToStored(n, shownFor(f.unit ?? "", prefs).unit, f.unit ?? "") : n;
      out.push({ metric: f.metric, value: stored ?? n });
    } else {
      out.push({ metric: f.metric, value_text: raw });
    }
  }
  return out;
}

/**
 * A scanned draft (the paper copy read into the register's fields, each in the unit its field stores) as the form
 * shows it: a number in the shown unit, written with the fewest figures that read back to the scanned value, so a draft
 * submitted as it was filled sends exactly what was scanned (#1190 review HIGH). Text, and a number the scan could not
 * read, stay as scanned; nothing changes where the choice converts nothing.
 */
export function draftInShownUnits<F extends EntryField>(
  fields: readonly F[],
  draft: Record<string, string>,
  prefs: DisplayPrefs | undefined,
): Record<string, string> {
  const out = { ...draft };
  for (const f of fields) {
    const raw = draft[f.metric];
    if (raw === undefined || !isNumericKind(f.data_kind) || raw.trim() === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    const s = shownFor(f.unit ?? "", prefs);
    if (s.converted) out[f.metric] = String(shownForFile(n, f.unit ?? "", prefs));
  }
  return out;
}

